// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useRef, useState } from "react";
import { Drawer, Input } from "antd";
import type { RoomEvent } from "../types";
import { reactionRow, MessageBody, type ReactControl } from "./Timeline";

// effectiveBody reads a message body from decrypted-or-plaintext content, so
// thread replies render whether or not they're E2E-encrypted.
function effectiveBody(ev: RoomEvent): string {
  const c = ev.decryptedContent ?? ev.content;
  return String((c as { body?: string }).body ?? "");
}

function nick(userID: string, names: Record<string, string>): string {
  return names[userID] || userID.replace(/^@/, "").split(":")[0];
}

// ThreadPanel is a side drawer showing a message and its threaded replies, with a
// composer that posts back into the thread (in-room sub-conversation).
export function ThreadPanel({
  root,
  replies,
  names,
  react,
  onSend,
  onClose,
}: {
  readonly root: RoomEvent;
  readonly replies: RoomEvent[];
  readonly names: Record<string, string>;
  readonly react: ReactControl;
  readonly onSend: (text: string) => void;
  readonly onClose: () => void;
}) {
  const [draft, setDraft] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [replies.length]);

  function submit() {
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft("");
  }

  return (
    <Drawer title="Thread" open onClose={onClose} width={360} className="thread-drawer">
      <div className="thread-messages">
        <div className="thread-root line-msg">
          <span className="nick">&lt;{nick(root.sender, names)}&gt;</span> <div className="msg md-body">{<MessageBody text={effectiveBody(root)} />}</div>
          {root.event_id && reactionRow(root.event_id, react)}
        </div>
        <div className="thread-divider">{replies.length} {replies.length === 1 ? "reply" : "replies"}</div>
        {replies.map((r) => (
          <div key={r.event_id} className="thread-reply line-msg">
            <span className="nick">&lt;{nick(r.sender, names)}&gt;</span> <div className="msg md-body">{<MessageBody text={effectiveBody(r)} />}</div>
            {r.event_id && reactionRow(r.event_id, react)}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <Input.TextArea
        className="thread-composer"
        placeholder="Reply in thread…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onPressEnter={(e) => {
          e.preventDefault();
          submit();
        }}
        autoSize={{ minRows: 1, maxRows: 4 }}
      />
    </Drawer>
  );
}
